import React, { useEffect } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import AppNavigation from "./AppNavigation";
import ChatView from "./views/ChatView";
import HomeView from "./views/HomeView";
import WalletView from "./views/WalletView";
import MarketView from "./views/MarketView";
import SettingsView from "./views/SettingsView";
import FoodView from "./views/FoodView";
import { useAppSelector } from "../../hooks/hooks";
import TokenView from "./views/TokenView";
import MooLoanView from "./views/MooLoanView";
import { AuthForm } from "../AuthForm"; // Make sure to import AuthForm
import BankingView from "./views/BankingView";

const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

const AppLayout: React.FC = () => {
  const theme = useTheme();
  const { token } = useAppSelector((state) => state.auth);
  const { currentNavigationView } = useAppSelector((state) => state.ui);
  const { isCompletingLogin } = useAppSelector((state) => state.ui);

  // Use the same breakpoint as AppNavigation for consistency
  const isMobileView = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);

  const renderView = () => {
    switch (currentNavigationView) {
      case "home":
        return <HomeView />;
      case "chat":
        return <ChatView />;
      case "wallet":
        return <WalletView />;
      case "market":
        return <MarketView />;
      case "food":
        return <FoodView />;
      case "settings":
        return <SettingsView />;
      case "token":
        return <TokenView />;
      case "mooloans":
        return <MooLoanView />;
      case "banking":
        return <BankingView />;
      default:
        return <ChatView />;
    }
  };

  // useEffect(() => {
  //   console.log("Token => ", token);
  //   console.log("isCompletingLogin => ", isCompletingLogin);
  // }, [token, isCompletingLogin]);

  // If no token, render only AuthForm
  if (!token && !isCompletingLogin) {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: MOBILE_APP_WIDTH,
          margin: "auto",
          minHeight: "100dvh",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          // Mobile-specific styles
          ...(isMobileView && {
            maxWidth: "100%",
            borderRadius: 0,
          }),
          // Desktop-specific styles
          ...(!isMobileView && {
            boxShadow: "0 0 20px rgba(0,0,0,0.1)",
            borderLeft: "1px solid rgba(0,0,0,0.1)",
            borderRight: "1px solid rgba(0,0,0,0.1)",
          }),
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: 2,
          }}
        >
          <AuthForm />
        </Box>
      </Box>
    );
  }

  // If token exists, render the normal app layout
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: MOBILE_APP_WIDTH,
        margin: "auto",
        paddingBottom: theme.mixins.toolbar.minHeight, // Dynamic padding
        minHeight: "100dvh",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        // Mobile-specific styles
        ...(isMobileView && {
          maxWidth: "100%",
          borderRadius: 0,
        }),
        // Desktop-specific styles
        ...(!isMobileView && {
          boxShadow: "0 0 20px rgba(0,0,0,0.1)",
          borderLeft: "1px solid rgba(0,0,0,0.1)",
          borderRight: "1px solid rgba(0,0,0,0.1)",
        }),
      }}
    >
      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          // Ensure proper scrolling behavior
          WebkitOverflowScrolling: "touch",
        }}
      >
        {renderView()}
      </Box>

      {/* Navigation - conditionally rendered */}
      {token && !isCompletingLogin && <AppNavigation />}
    </Box>
  );
};

export default AppLayout;
